/**
 * Node visual de Teste A/B para o Flow Builder
 * Distribui fluxo entre múltiplas saídas baseado em porcentagens configuradas
 * Inclui toolbar flutuante ao selecionar
 */

import { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { Shuffle } from 'lucide-react';
import { FlowNodeData, ABTestConfig } from '@/types/flows';
import { FlowNodeToolbar } from './FlowNodeToolbar';

/**
 * Componente visual do node Teste A/B
 * Exibe variantes configuradas com porcentagens e handles de saída dinâmicos
 */
export const ABTestNode = memo(({ data, id, selected }: NodeProps) => {
  const nodeData = data as FlowNodeData;
  const config = (nodeData.config as ABTestConfig) || { variants: [] };
  const variants = config.variants || [
    { id: 'variant_0', label: 'A', percentage: 50 },
    { id: 'variant_1', label: 'B', percentage: 50 },
  ];
  
  // Callbacks injetados via FlowCanvas
  const onEdit = (nodeData as any).onEdit;
  const onDuplicate = (nodeData as any).onDuplicate;
  const onDelete = (nodeData as any).onDelete;
  const onTestFromHere = (nodeData as any).onTestFromHere;
  
  // Verifica conexões de edges (injetado pelo FlowCanvas)
  const edges = (data as any).edges || [];
  
  /**
   * Verifica se uma variante tem conexão de saída
   */
  const hasConnection = (variantIndex: number) => {
    return edges.some((e: any) => 
      e.source === id && e.sourceHandle === `variant_${variantIndex}`
    );
  };

  return (
    <div className="bg-card border-2 border-pink-500/50 rounded-lg shadow-lg min-w-[200px]">
      {/* Toolbar flutuante */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onTestFromHere={onTestFromHere}
        isVisible={selected}
      />
      
      {/* Handle de entrada */}
      <Handle
        type="target"
        position={Position.Top}
        className="w-3 h-3 bg-pink-500 border-2 border-background"
      />

      {/* Header */}
      <div className="flex items-center gap-2 p-3 border-b border-border bg-pink-500/10 rounded-t-lg">
        <div className="p-1.5 rounded bg-pink-500">
          <Shuffle className="h-4 w-4 text-white" />
        </div>
        <span className="font-semibold text-sm">{(data as any).config?.nickname || 'Teste A/B'}</span>
      </div>

      {/* Lista de variantes */}
      <div className="p-2 space-y-2">
        {variants.map((variant, index) => {
          const connected = hasConnection(index);
          
          return (
            <div 
              key={variant.id}
              className="relative flex items-center justify-between p-2 bg-muted/50 rounded border border-border"
            >
              {/* Label e porcentagem */}
              <div className="flex items-center gap-2">
                <span className="font-bold text-sm bg-pink-500 text-white px-2 py-0.5 rounded">
                  {variant.label}
                </span>
                <span className="text-xs text-muted-foreground">
                  {variant.percentage}%
                </span>
              </div>
              
              {/* Handle de saída para esta variante */}
              <Handle
                type="source"
                position={Position.Right}
                id={`variant_${index}`}
                className={`w-3 h-3 border-2 border-background ${
                  connected 
                    ? 'bg-pink-500' 
                    : 'bg-destructive animate-pulse'
                }`}
                style={{ 
                  right: -6,
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              />
            </div>
          );
        })}
      </div>

      {/* ID do node para referência */}
      <div className="px-3 pb-2">
        <span className="text-[10px] text-muted-foreground font-mono">
          {id}
        </span>
      </div>
    </div>
  );
});

ABTestNode.displayName = 'ABTestNode';
